/**
 * Photo Studio Service
 *
 * Orchestrates product photo generation and library saving for the
 * Product Photo Studio page. Uses the same Gemini image generation
 * endpoint and retry patterns as the bulk generation service.
 */

import { saveLibraryItem } from './libraryService';

const SERVER_URL = 'http://localhost:3001';

const RATE_LIMIT_CONFIG = {
    delayBetweenRequests: 15000,
    maxRetries: 5,
    initialRetryDelay: 30000,
    maxRetryDelay: 180000
};

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetch with automatic retry on rate limit / service unavailable.
 */
async function fetchWithRetry(url, options, retryCount = 0, onRetry = null) {
    try {
        const response = await fetch(url, options);

        if (response.status === 429 || response.status === 503 || response.status === 422) {
            if (retryCount >= RATE_LIMIT_CONFIG.maxRetries) {
                throw new Error('Rate limit exceeded after maximum retries. Please try again later.');
            }

            const retryDelay = Math.min(
                RATE_LIMIT_CONFIG.initialRetryDelay * Math.pow(2, retryCount),
                RATE_LIMIT_CONFIG.maxRetryDelay
            );

            const retryMessage = `Rate limited. Retrying in ${Math.round(retryDelay / 1000)}s... (attempt ${retryCount + 1}/${RATE_LIMIT_CONFIG.maxRetries})`;
            console.log(retryMessage);

            onRetry?.({
                type: 'rate_limit',
                attempt: retryCount + 1,
                maxAttempts: RATE_LIMIT_CONFIG.maxRetries,
                retryInSeconds: Math.round(retryDelay / 1000),
                message: retryMessage
            });

            await delay(retryDelay);
            return fetchWithRetry(url, options, retryCount + 1, onRetry);
        }

        return response;
    } catch (error) {
        if (error.message.includes('Rate limit exceeded')) {
            throw error;
        }

        if (retryCount < RATE_LIMIT_CONFIG.maxRetries) {
            const retryDelay = Math.min(
                RATE_LIMIT_CONFIG.initialRetryDelay * Math.pow(2, retryCount),
                RATE_LIMIT_CONFIG.maxRetryDelay
            );
            console.log(`Network error, retrying in ${Math.round(retryDelay / 1000)}s...`);
            onRetry?.({
                type: 'network_error',
                attempt: retryCount + 1,
                maxAttempts: RATE_LIMIT_CONFIG.maxRetries,
                retryInSeconds: Math.round(retryDelay / 1000),
                message: `Network error. Retrying in ${Math.round(retryDelay / 1000)}s...`
            });
            await delay(retryDelay);
            return fetchWithRetry(url, options, retryCount + 1, onRetry);
        }

        throw error;
    }
}

/**
 * Generate a product photo via the Gemini image generation endpoint.
 *
 * @param {Object} params
 * @param {string} params.prompt - The constructed prompt
 * @param {Array} params.referenceImages - Array of { url, instruction }
 * @param {string} params.aspectRatio - e.g. '1:1', '4:5'
 * @param {string} params.userId - For billing/credits
 * @param {Function} params.onRetry - Optional retry callback
 * @returns {Promise<{ url: string, generatedAt: string }>}
 */
export async function generateProductPhoto({ prompt, referenceImages = [], aspectRatio = '1:1', userId, onRetry = null }) {
    const formattedReferenceImages = referenceImages
        .filter(img => img?.url)
        .map(img => ({
            url: img.url,
            instruction: img.instruction || 'Use this product image as visual reference.'
        }));

    const response = await fetchWithRetry(`${SERVER_URL}/api/generate-image-gemini`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            prompt,
            userId,
            aspectRatio,
            referenceImages: formattedReferenceImages.length > 0 ? formattedReferenceImages : undefined
        })
    }, 0, onRetry);

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || error.message || 'Image generation failed');
    }

    const data = await response.json();
    return {
        url: data.url,
        generatedAt: data.generatedAt || new Date().toISOString()
    };
}

/**
 * Save a generated photo to the asset library with photo-studio metadata.
 *
 * @param {Object} params
 * @param {string} params.imageUrl - Generated image URL
 * @param {string} params.prompt - The prompt used
 * @param {string} params.aspectRatio - Aspect ratio used
 * @param {Object} params.product - Product data (optional)
 * @param {Object} params.scene - Scene preset used (optional)
 * @param {string} params.userId - User ID
 * @param {string} params.brandId - Brand ID
 * @returns {Promise<string>} Library item ID
 */
export async function savePhotoToLibrary({ imageUrl, prompt, aspectRatio, product, scene, userId, brandId }) {
    const productName = product?.name || 'Product';
    const sceneName = scene?.name || 'Custom';

    return saveLibraryItem({
        name: `${productName} — ${sceneName} Photo`,
        type: 'image',
        imageUrl,
        thumbnailUrl: imageUrl,
        tags: ['photo-studio', scene?.id || 'custom'].filter(Boolean),
        metadata: {
            prompt,
            outputType: 'image',
            aspectRatio,
            productName: product?.name || null,
            generatedAt: new Date().toISOString(),
            source: 'photo-studio',
            sceneType: scene?.id || 'custom',
        }
    }, userId, brandId);
}

/**
 * Delay between requests (for multi-variant generation).
 */
export { delay, RATE_LIMIT_CONFIG };
